import * as React from 'react';
import queryString from 'query-string';
import { ScrollContainer } from 'components/ScrollContainer';
import FilterPanel from 'components/FilterPanel';
import {
  SecondaryContent,
  OrderedItemWrapper,
  SecondaryContentWrapper,
} from './Analysis.styles';
import BreakdownByContainer from './BreakdownByContainer';
import HeadlineContainer from './HeadlineContainer';
import SpendByContainer from './SpendByContainer';
import SpendByJobContainer from './SpendByJob/SpendByJobContainer';
import TopJobRolesContainer from './TopJobRolesContainer';
import TopVendorsContaiiner from './TopVendorsContainer';
import TopSpendItemsContainer from './TopSpendItemsContainer';
import YearOnYearComparisonContainer from './YearOnYearComparisonContainer';
import { ApiContext } from 'context/ApiContext';
import {
  PersonalizeOrder,
  isConfigItemVisible,
  getConfigIndex,
} from 'components/PersonalizeOrder';
import {
  IBreakdownByResponse,
  IDisplayByType,
  IHeadcountSpend,
  IHeadlinesResponse,
  ISelectOption,
  ISettingsResponse,
  ISpendByType,
  ISpendContribution,
  ISpendContributionResponse,
  ITopItemsResponse,
  ITopJobRolesResponse,
  ITopVendorsResponse,
  IYearOnYearResponse,
} from 'utils/types';
import QueryParams, { getQueryDatesFromPeriod } from 'context/QueryParams';
import {
  BREAKDOWN_BY,
  CHART_TYPES,
  CHART_TYPES_CONSTANTS,
  CONTRIBUTIONS,
  DISPLAY_BY_TO_SPEND_VIEW_TYPE,
  PERMISSIONS,
  SPEND_BY_OPTIONS,
  SPEND_BY_TO_API,
} from 'utils/constants';
import { NoPermissions } from 'App.styles';
import groupBy from 'lodash/groupBy';
import NoData from 'views/NoData/NoData';
import isEqual from 'lodash/isEqual';
import NotificationBar from 'components/NotificationBar/NotificationBar';
import { orderBy } from 'lodash';
import {
  fetchSpendByContribution,
  getJobSpend,
  exportSpendData,
  exportSpendYoYData,
  exportSpendTopVendorsData,
  exportSpendBreakdownChartData,
  exportSpendHeadlineData,
  exportSpendTopThirdPartyData,
  exportSpendHeadcountData,
  exportSpendHeadcountTableData,
  exportSpendBreakdownBusinessUnitData,
  exportSpendBreakdownGeographyData,
  fetchHeadlines,
  fetchYearOnYear,
  fetchBreakdownChart,
  fetchHeadcountSpend,
  fetchTopVendors,
  fetchTopItems,
} from 'utils/api';
import { getLocalStorageItem, setLocalStorageItem } from 'utils/localStorage';
import ChartOptions from 'components/ChartOptions/ChartOptions';
import moment from 'moment';

const NO_PERMISSIONS_TEXT = 'You do not have permission to view this page';

const LOCAL_STORAGE_KEY = 'TXR_SPEND_ANALYSIS_PARAMS_KEY';
const FILTERS_LOCAL_STORAGE_KEY = 'TXR_SPEND_ANALYSIS_FILTERS_KEY';

interface IAnalysisViewProps {
  params: { [key: string]: string | string[] };
  selectedSpendBy?: string;
  serviceTowerL2?: string;
  setParams: (p: any) => void;
  settings: ISettingsResponse;
  spendBy: ISpendByType;
  permissions: string[];
  getSpendAnalysisData: () => void;
  updateAppState: (p: { [key: string]: any }) => void;
}

interface IAnalysisViewConfigState {
  chartOption: string;
  contributions?: string;
  displayBy: IDisplayByType;
  permanentContractorChecked: boolean;
  spendBy: ISpendByType;
  serviceTowerL2?: string;
  selectedBreakdownBy: string;
  selectedSpendBy?: string;
}

interface IAnalysisViewState extends IAnalysisViewConfigState {
  analysisLoading: boolean;
  loading: boolean;
  isSuccessNotification: boolean;
  breakdownBy?: IBreakdownByResponse;
  headcountSpend: IHeadcountSpend[];
  headlines?: IHeadlinesResponse;
  spendItems?: ISpendContribution[];
  topJobRoles?: ITopJobRolesResponse;
  topVendors?: ITopVendorsResponse;
  topItems?: ITopItemsResponse;
  yearOnYear?: IYearOnYearResponse;
}

class AnalysisView extends React.Component<
  IAnalysisViewProps,
  IAnalysisViewState
> {
  state = {
    analysisLoading: true,
    contributions: undefined,
    loading: true,
    isSuccessNotification: false,
    breakdownBy: undefined,
    headcountSpend: [],
    headlines: undefined,
    spendItems: [],
    topJobRoles: undefined,
    topVendors: undefined,
    topItems: undefined,
    yearOnYear: undefined,
    ...this.getDefaultConfig(),
  };

  spendByChartRef = React.createRef<any>();

  getDefaultConfig() {
    const localStorageState = getLocalStorageItem(LOCAL_STORAGE_KEY);
    const defaultState: IAnalysisViewConfigState = {
      chartOption: CHART_TYPES[1].value,
      contributions: undefined,
      displayBy: IDisplayByType.COST,
      permanentContractorChecked: false,
      selectedBreakdownBy: BREAKDOWN_BY.EXPENSE_TYPE,
      serviceTowerL2: undefined,
      selectedSpendBy: undefined,
      spendBy: ISpendByType.SERVICE_TOWER,
    };

    return {
      ...defaultState,
      ...localStorageState,
    } as IAnalysisViewConfigState;
  }

  setStateToLocalStorage = () => {
    const {
      chartOption,
      contributions,
      displayBy,
      permanentContractorChecked,
      selectedBreakdownBy,
      selectedSpendBy,
      serviceTowerL2,
      spendBy,
    } = this.state;

    setLocalStorageItem(LOCAL_STORAGE_KEY, {
      chartOption,
      contributions,
      displayBy,
      permanentContractorChecked,
      selectedBreakdownBy,
      serviceTowerL2,
      selectedSpendBy,
      spendBy,
    });
  };

  showSuccessNotification = () => {
    this.setState({ isSuccessNotification: true });
    setTimeout(() => {
      this.setState({ isSuccessNotification: false });
    }, 5000);
  };

  isPersonnelBecomeSelected = (prevState: IAnalysisViewState) => {
    const { contributions } = this.state;
    return (
      contributions === CONTRIBUTIONS.PERSONNEL &&
      prevState.contributions !== CONTRIBUTIONS.PERSONNEL
    );
  };

  isPersonnelBecomeUnSelected = (prevState: IAnalysisViewState) => {
    const { contributions } = this.state;
    return (
      contributions !== CONTRIBUTIONS.PERSONNEL &&
      prevState.contributions === CONTRIBUTIONS.PERSONNEL
    );
  };

  isSettingsAvailable = () => {
    const { settings } = this.props;
    return (
      settings &&
      settings.availableRange &&
      settings.availableRange.startYear &&
      settings.availableRange.endYear
    );
  };

  getSpendByChartInstance = () => {
    return (
      this.spendByChartRef &&
      this.spendByChartRef.current &&
      this.spendByChartRef.current.chart
    );
  };

  handleExport = (chartInstance: any, filename: string) => {
    if (chartInstance) {
      chartInstance.exportChart({ filename }, { chart: { width: 1200 } });
    }
  };

  exportSpendBy = (val: any) => {
    const { displayBy } = this.state;
    if (val.value === 'png') {
      this.handleExport(
        this.getSpendByChartInstance(),
        `TXR_summary-${moment().format()}`
      );
    }
    if (val.value === 'xlsx') {
      this.downloadSpendData({ displayBy }).then(this.showSuccessNotification);
    }
  };

  updateDisplayBy = (displayBy: IDisplayByType) => {
    this.setState({ displayBy });
  };

  // Method to fetch data with standard query params
  getQueryParams = (noSpendByParam = false) => {
    const { contributions, selectedSpendBy, serviceTowerL2, spendBy } =
      this.state;
    const { params, settings } = this.props;

    // @ts-ignore
    if (settings && settings?.selectedYear) {
      const { startDate, endDate } = getQueryDatesFromPeriod(
        // @ts-ignore
        settings.selectedYear
      );

      const searchQuery = queryString.stringify({ startDate, endDate });
      let contributionsQuery = '';
      let selectedQuery = '';
      let serviceTowerL2Query = '';
      let filtersQuery = '';
      if (selectedSpendBy && !noSpendByParam) {
        selectedQuery = `&${queryString.stringify({
          [SPEND_BY_TO_API[spendBy]]: selectedSpendBy,
        })}`;
      }
      if (serviceTowerL2) {
        serviceTowerL2Query = `&${queryString.stringify({ serviceTowerL2 })}`;
      }
      if (Object.keys(params).length !== 0) {
        // Remove empty parameters from query
        const query = Object.fromEntries(
          Object.entries(params).filter(([_, v]) => v != null)
        );
        filtersQuery = `&${queryString.stringify(query, {
          arrayFormat: 'index',
        })}`;
      }
      if (contributions) {
        contributionsQuery = `&contributions=${contributions}`;
      }

      return `${searchQuery}${selectedQuery}${filtersQuery}${serviceTowerL2Query}${contributionsQuery}`;
    }
    return '';
  };

  getSplitPermContractorQueryParams = () => {
    const { permanentContractorChecked } = this.state;
    let query = this.getQueryParams();
    if (permanentContractorChecked) {
      return `${query}&splitByPermOrContractor=true`;
    }
    return `${query}&splitByPermOrContractor=false`;
  };

  getSpendByContribution = () => {
    return fetchSpendByContribution(
      this.state.spendBy,
      this.getQueryParams(true)
    ).then((spendResponse: ISpendContributionResponse) => {
      this.setState({
        spendItems: orderBy(spendResponse.spendItems, 'cost', 'desc'),
      });
    });
  };

  getJobSpend = () => {
    const { permissions } = this.props;
    const hasPersonnelPermission = permissions.includes(
      PERMISSIONS.VIEW_PERSONNEL
    );

    return hasPersonnelPermission
      ? getJobSpend(this.getQueryParams()).then(
          (topJobRoles: ITopJobRolesResponse) => {
            this.setState({ topJobRoles });
          }
        )
      : Promise.resolve();
  };

  downloadSpendData = ({ displayBy }: { displayBy: IDisplayByType }) => {
    const { selectedSpendBy, spendBy } = this.state;
    let query = `spendViewType=${
      DISPLAY_BY_TO_SPEND_VIEW_TYPE[displayBy]
    }&${this.getSplitPermContractorQueryParams()}`;
    if (selectedSpendBy && spendBy === ISpendByType.SERVICE_TOWER) {
      query = `${query}&dimValueL1=${selectedSpendBy}`;
    }
    return exportSpendData(spendBy, query);
  };

  downloadSpendYoYData = () => exportSpendYoYData(this.getQueryParams());

  downloadSpendTopVendorsData = () =>
    exportSpendTopVendorsData(this.getQueryParams());

  downloadSpendBreakdownChartData = (dimension: string) =>
    exportSpendBreakdownChartData(dimension, this.getQueryParams());

  downloadSpendHeadlineData = () =>
    exportSpendHeadlineData(this.getQueryParams());

  downloadSpendTopThirdPartyData = () =>
    exportSpendTopThirdPartyData(this.getQueryParams());

  downloadSpendHeadcountData = (spendView: string) =>
    exportSpendHeadcountData(
      spendView,
      this.getSplitPermContractorQueryParams()
    );

  downloadSpendHeadcountTableData = (spendView: string) =>
    exportSpendHeadcountTableData(
      spendView,
      this.getSplitPermContractorQueryParams()
    );

  downloadSpendBreakdownBusinessUnitData = (dimension: string) =>
    exportSpendBreakdownBusinessUnitData(dimension, this.getQueryParams());

  downloadSpendGeographyData = (dimension: string) =>
    exportSpendBreakdownGeographyData(dimension, this.getQueryParams());

  getSpendAnalysisData = () => {
    const { settings } = this.props;
    const { contributions } = this.state;
    const isPersonnel = contributions === CONTRIBUTIONS.PERSONNEL;
    this.setStateToLocalStorage();
    if (settings) {
      this.setState({ analysisLoading: true });
      this.getSpendByContribution()
        .then(this.fetchHeadlines)
        .then(() => this.setState({ analysisLoading: false }))
        .then(() => (isPersonnel ? Promise.resolve() : this.fetchTopItems()))
        .then(() =>
          Promise.all([this.getYearOnYear(), this.fetchBreakdownBy()])
        )
        .then(() =>
          Promise.all([
            isPersonnel ? Promise.resolve() : this.fetchTopVendors(),
            isPersonnel ? this.fetchHeadcountSpend() : Promise.resolve(),
            isPersonnel ? this.getJobSpend() : Promise.resolve(),
          ])
        )
        .then(() => this.setState({ loading: false }))
        .catch((e) => {
          console.error(
            'Fetching spend analysis data failed',
            JSON.stringify(e)
          );
        });
    }
  };

  fetchHeadlines = () => {
    return fetchHeadlines(this.getQueryParams()).then(
      (headlinesResponse: IHeadlinesResponse) => {
        this.setState({ headlines: headlinesResponse });
      }
    );
  };

  getYearOnYear = () => {
    return fetchYearOnYear(this.getQueryParams()).then(
      (yearOnYearResponse: IYearOnYearResponse) => {
        this.setState({ yearOnYear: yearOnYearResponse });
      }
    );
  };

  fetchBreakdownBy = () => {
    return fetchBreakdownChart(this.getQueryParams()).then(
      (breakdownByResponse: IBreakdownByResponse) => {
        this.setState({ breakdownBy: breakdownByResponse });
      }
    );
  };

  fetchHeadcountSpend = () => {
    const { permissions } = this.props;
    const hasPersonnelPermission = permissions.includes(
      PERMISSIONS.VIEW_PERSONNEL
    );

    return hasPersonnelPermission
      ? fetchHeadcountSpend(this.getQueryParams()).then(
          (response: IHeadcountSpend[]) => {
            this.setState({ headcountSpend: response });
          }
        )
      : Promise.resolve();
  };

  fetchTopVendors = () => {
    return fetchTopVendors(this.getQueryParams()).then(
      (topVendorsResponse: ITopVendorsResponse) => {
        this.setState({ topVendors: topVendorsResponse });
      }
    );
  };

  fetchTopItems = () => {
    return fetchTopItems(this.getQueryParams()).then(
      (topItemsResponse: ITopItemsResponse) => {
        this.setState({ topItems: topItemsResponse });
      }
    );
  };

  updateAppState = (state: IAnalysisViewState, callback?: () => void) => {
    this.setState({ ...state }, () => {
      if (callback) {
        callback();
      }
    });
  };

  componentDidMount() {
    if (this.isSettingsAvailable()) {
      this.getSpendAnalysisData();
    }
  }

  componentDidUpdate(
    prevProps: IAnalysisViewProps,
    prevState: IAnalysisViewState
  ) {
    const { permissions } = this.props;
    const { contributions } = this.state;
    if (this.isPersonnelBecomeSelected(prevState)) {
      if (this.state.spendBy === ISpendByType.VENDORS) {
        this.setState(
          {
            spendBy: ISpendByType.SERVICE_TOWER,
            selectedSpendBy: undefined,
            serviceTowerL2: undefined,
          },
          () => {
            this.getSpendAnalysisData();
          }
        );
      }
    } else if (this.isPersonnelBecomeUnSelected(prevState)) {
      this.setState(
        {
          displayBy: IDisplayByType.COST,
          selectedSpendBy: undefined,
          serviceTowerL2: undefined,
        },
        () => {
          this.getSpendAnalysisData();
        }
      );
    }
    if (
      !permissions.includes(PERMISSIONS.VIEW_PERSONNEL) &&
      contributions === CONTRIBUTIONS.PERSONNEL
    ) {
      this.setState({ contributions: CONTRIBUTIONS.THIRD_PARTY });

      return;
    }
    if (
      !isEqual(prevProps.params, this.props.params) ||
      !isEqual(this.props.settings, prevProps.settings)
    ) {
      this.getSpendAnalysisData();
    }
  }

  render() {
    const { permissions, settings } = this.props;
    const {
      analysisLoading,
      breakdownBy,
      chartOption,
      contributions,
      displayBy,
      headlines,
      isSuccessNotification,
      loading,
      spendBy,
      spendItems,
      headcountSpend,
      permanentContractorChecked,
      selectedBreakdownBy,
      selectedSpendBy,
      serviceTowerL2,
      topJobRoles,
      topItems,
      topVendors,
      yearOnYear,
    } = this.state;
    const isPersonnelAvailable = permissions.includes(
      PERMISSIONS.VIEW_PERSONNEL
    );
    const isPersonnel = contributions === CONTRIBUTIONS.PERSONNEL;
    const selectedSpendByOption =
      SPEND_BY_OPTIONS.find((option) => option.value === spendBy) ||
      SPEND_BY_OPTIONS[0];
    const isOnlyContributionData =
      Object.keys(groupBy(spendItems, 'contribution')).length === 1;
    const hasViewPermission = permissions.includes(
      PERMISSIONS.SPEND_ANALYSIS_READ
    );

    if (!hasViewPermission) {
      return <NoPermissions>{NO_PERMISSIONS_TEXT}</NoPermissions>;
    }

    if (!settings) {
      return null;
    }
    return this.isSettingsAvailable() ? (
      <>
        <FilterPanel localStorageKey={FILTERS_LOCAL_STORAGE_KEY} />
        <ScrollContainer>
          <ChartOptions
            contributions={contributions}
            onExport={this.exportSpendBy}
            isPersonnel={isPersonnel}
            displayBy={displayBy}
            selectedChartOption={chartOption}
            spendBy={spendBy}
            permanentContractorChecked={permanentContractorChecked}
            onDisplayByChange={(displayByOption: ISelectOption) => {
              this.setState(
                {
                  displayBy: displayByOption.value as IDisplayByType,
                },
                this.getSpendAnalysisData
              );
            }}
            onSpendChange={(selectedSpendByOption: ISelectOption) => {
              this.setState(
                {
                  spendBy: selectedSpendByOption.value as ISpendByType,
                  selectedSpendBy: undefined,
                  serviceTowerL2: undefined,
                },
                this.getSpendAnalysisData
              );
            }}
            onSpendContributionChange={(contributionsOption: ISelectOption) => {
              const isMapView = chartOption === CHART_TYPES_CONSTANTS.MAP;
              this.setState(
                {
                  contributions: contributionsOption.value as ISpendByType,
                  chartOption: isMapView
                    ? CHART_TYPES_CONSTANTS.STACKED_COLUMN
                    : chartOption,
                },
                this.getSpendAnalysisData
              );
            }}
            onChartChange={(chartOption: ISelectOption) => {
              this.setState(
                {
                  chartOption: chartOption.value,
                },
                this.setStateToLocalStorage
              );
            }}
            onPermanentContractorChange={() => {
              this.setState(
                { permanentContractorChecked: !permanentContractorChecked },
                this.setStateToLocalStorage
              );
            }}
            selectedL1={selectedSpendBy}
            selectedL2={serviceTowerL2}
            clearL1={() => {
              this.setState(
                { selectedSpendBy: undefined },
                this.getSpendAnalysisData
              );
            }}
            clearL2={() => {
              this.setState(
                { serviceTowerL2: undefined },
                this.getSpendAnalysisData
              );
            }}
          />
          <SpendByContainer
            analysisLoading={analysisLoading}
            chartOption={chartOption}
            chartRef={this.spendByChartRef}
            contributions={contributions}
            displayBy={displayBy}
            headlines={headlines}
            loading={loading}
            permanentContractorChecked={permanentContractorChecked}
            selectedSpendBy={selectedSpendBy}
            serviceTowerL2={serviceTowerL2}
            spendBy={spendBy}
            spendItems={spendItems}
            setStateToLocalStorage={this.setStateToLocalStorage}
            showSuccessNotification={this.showSuccessNotification}
            updateAppState={this.updateAppState}
            getSpendAnalysisData={this.getSpendAnalysisData}
          />
          <SecondaryContentWrapper>
            <SecondaryContent>
              <PersonalizeOrder>
                {(configItems) => (
                  <>
                    {isConfigItemVisible(configItems, 'Headline') && (
                      <OrderedItemWrapper
                        id="Headline"
                        order={getConfigIndex(configItems, 'Headline')}
                      >
                        <HeadlineContainer
                          headlines={headlines}
                          downloadSpendHeadlineData={
                            this.downloadSpendHeadlineData
                          }
                          showSuccessNotification={this.showSuccessNotification}
                        />
                      </OrderedItemWrapper>
                    )}

                    {isPersonnel &&
                      isPersonnelAvailable &&
                      isConfigItemVisible(configItems, 'TopJobRoles') && (
                        <OrderedItemWrapper
                          id="TopJobRoles"
                          order={getConfigIndex(configItems, 'TopJobRoles')}
                        >
                          <TopJobRolesContainer
                            displayBy={displayBy}
                            downloadSpendHeadcountTableData={
                              this.downloadSpendHeadcountTableData
                            }
                            permanentContractorChecked={
                              permanentContractorChecked
                            }
                            topJobRoles={topJobRoles}
                            showSuccessNotification={
                              this.showSuccessNotification
                            }
                          />
                        </OrderedItemWrapper>
                      )}

                    {isConfigItemVisible(
                      configItems,
                      'YearOnYearComparison'
                    ) && (
                      <OrderedItemWrapper
                        id="YearOnYearComparison"
                        order={getConfigIndex(
                          configItems,
                          'YearOnYearComparison'
                        )}
                      >
                        <YearOnYearComparisonContainer
                          downloadSpendYoYData={this.downloadSpendYoYData}
                          yearOnYear={yearOnYear}
                          onExportChart={this.handleExport}
                          showSuccessNotification={this.showSuccessNotification}
                        />
                      </OrderedItemWrapper>
                    )}

                    {isConfigItemVisible(configItems, 'BreakdownBy') && (
                      <OrderedItemWrapper
                        id="BreakdownBy"
                        order={getConfigIndex(configItems, 'BreakdownBy')}
                      >
                        <BreakdownByContainer
                          breakdownBy={breakdownBy}
                          downloadSpendBreakdownChartData={
                            this.downloadSpendBreakdownChartData
                          }
                          downloadSpendBreakdownBusinessUnitData={
                            this.downloadSpendBreakdownBusinessUnitData
                          }
                          downloadSpendGeographyData={
                            this.downloadSpendGeographyData
                          }
                          selectedBreakdownBy={selectedBreakdownBy}
                          updateSelectedBreakdownBy={(
                            selectedBreakdownBy: string
                          ) => {
                            this.setState(
                              { selectedBreakdownBy },
                              this.setStateToLocalStorage
                            );
                          }}
                          spendBy={spendBy}
                          isOnlyContribution={isOnlyContributionData}
                          showSuccessNotification={this.showSuccessNotification}
                        />
                      </OrderedItemWrapper>
                    )}

                    {selectedSpendByOption.value !== 'Vendor' &&
                      !isPersonnel &&
                      isConfigItemVisible(configItems, 'TopVendors') && (
                        <OrderedItemWrapper
                          id="TopVendors"
                          order={getConfigIndex(configItems, 'TopVendors')}
                        >
                          <TopVendorsContaiiner
                            downloadSpendTopVendorsData={
                              this.downloadSpendTopVendorsData
                            }
                            topVendors={topVendors}
                            showSuccessNotification={
                              this.showSuccessNotification
                            }
                          />
                        </OrderedItemWrapper>
                      )}

                    {isPersonnel &&
                      isPersonnelAvailable &&
                      isConfigItemVisible(configItems, 'SpendByJob') && (
                        <OrderedItemWrapper
                          id="SpendByJob"
                          order={getConfigIndex(configItems, 'SpendByJob')}
                        >
                          <SpendByJobContainer
                            displayBy={displayBy}
                            downloadSpendHeadcountData={
                              this.downloadSpendHeadcountData
                            }
                            headlines={headlines}
                            headcountSpend={headcountSpend}
                            permanentContractorChecked={
                              permanentContractorChecked
                            }
                            selectedSpendBy={selectedSpendBy}
                            serviceTowerL2={serviceTowerL2}
                            onExportChart={this.handleExport}
                            showSuccessNotification={
                              this.showSuccessNotification
                            }
                          />
                        </OrderedItemWrapper>
                      )}

                    {!isPersonnel &&
                      isConfigItemVisible(configItems, 'TopSpendItems') && (
                        <OrderedItemWrapper
                          id="TopSpendItems"
                          order={getConfigIndex(configItems, 'TopSpendItems')}
                        >
                          <TopSpendItemsContainer
                            downloadSpendTopThirdPartyData={
                              this.downloadSpendTopThirdPartyData
                            }
                            topItems={topItems}
                            showSuccessNotification={
                              this.showSuccessNotification
                            }
                          />
                        </OrderedItemWrapper>
                      )}
                  </>
                )}
              </PersonalizeOrder>
              {isSuccessNotification && (
                <NotificationBar successMessage="Generating file... You will receive a notification when the file has been generated" />
              )}
            </SecondaryContent>
          </SecondaryContentWrapper>
        </ScrollContainer>
      </>
    ) : (
      <NoData />
    );
  }
}

export const Analysis = (props: any) => (
  <QueryParams>
    {({ params, setParams }) => (
      <ApiContext.Consumer>
        {({ permissions, settings }) => (
          <AnalysisView
            {...props}
            params={params}
            permissions={permissions}
            setParams={setParams}
            settings={settings}
          />
        )}
      </ApiContext.Consumer>
    )}
  </QueryParams>
);

export default Analysis;
